import React, {
    createContext,
    useContext,
    useRef,
    useCallback,
    useState,
} from 'react'
import { driver, Driver, DriveStep } from 'driver.js'
import 'driver.js/dist/driver.css'

export interface TourStep {
    selector: string
    content: string
    title?: string
    position?: 'top' | 'right' | 'bottom' | 'left'
}

interface TourContextType {
    /** Start tour with steps from provider prop */
    start: () => void
    /** Start tour with custom steps */
    startTour: (steps: TourStep[], onComplete?: () => void) => void
    /** Exit current tour */
    exit: () => void
    exitTour: () => void
    /** Is tour currently active */
    isActive: boolean
    /** Current step index (0-based) */
    currentStep: number
    /** Set current step */
    setCurrentStep: (step: number) => void
}

const TourContext = createContext<TourContextType>({
    start: () => {},
    startTour: () => {},
    exit: () => {},
    exitTour: () => {},
    isActive: false,
    currentStep: 0,
    setCurrentStep: () => {},
})

export const useTour = () => {
    const context = useContext(TourContext)
    if (!context) {
        throw new Error('useTour must be used within a TourProvider')
    }
    return context
}

interface TourProviderProps {
    children: React.ReactNode
    /** Steps for backward compatibility with old API */
    steps?: TourStep[]
}

export const TourProvider: React.FC<TourProviderProps> = ({
    children,
    steps: providerSteps,
}) => {
    const driverRef = useRef<Driver | null>(null)
    const [isActive, setIsActive] = useState(false)
    const [currentStep, setCurrentStepState] = useState(0)

    const startTour = useCallback(
        (steps: TourStep[], onComplete?: () => void) => {
            // Exit any existing tour
            if (driverRef.current) {
                driverRef.current.destroy()
            }

            // Convert to driver.js format
            const driverSteps: DriveStep[] = steps.map((step) => ({
                element: step.selector,
                popover: {
                    title: step.title || '',
                    description: step.content,
                    side: step.position || 'bottom',
                    align: 'center' as const,
                },
            }))

            // Create driver instance with custom styling
            const driverInstance = driver({
                showProgress: true,
                showButtons: ['next', 'previous', 'close'],
                steps: driverSteps,
                nextBtnText: 'Tiếp theo',
                prevBtnText: 'Trước',
                doneBtnText: 'Hoàn thành',
                progressText: '{{current}} / {{total}}',
                popoverClass: 'driverjs-theme',
                onHighlightStarted: (_element, step) => {
                    const index = driverSteps.findIndex((s) => s === step)
                    if (index >= 0) setCurrentStepState(index)
                },
                onDestroyStarted: () => {
                    setIsActive(false)
                    driverInstance.destroy()
                },
                onDestroyed: () => {
                    setIsActive(false)
                    setCurrentStepState(0)
                    onComplete?.()
                },
            })

            driverRef.current = driverInstance
            setIsActive(true)
            driverInstance.drive()
        },
        []
    )

    // Backward compatible start() - uses steps from provider prop
    const start = useCallback(() => {
        if (providerSteps && providerSteps.length > 0) {
            startTour(providerSteps)
        } else {
            console.warn(
                'TourProvider: No steps provided. Use steps prop or startTour(steps).'
            )
        }
    }, [providerSteps, startTour])

    const exit = useCallback(() => {
        if (driverRef.current) {
            driverRef.current.destroy()
            driverRef.current = null
        }
        setIsActive(false)
        setCurrentStepState(0)
    }, [])

    const setCurrentStep = useCallback((step: number) => {
        if (driverRef.current && step >= 0) {
            driverRef.current.moveTo(step)
            setCurrentStepState(step)
        }
    }, [])

    return (
        <TourContext.Provider
            value={{
                start,
                startTour,
                exit,
                exitTour: exit,
                isActive,
                currentStep,
                setCurrentStep,
            }}
        >
            {children}
            {/* Custom CSS for driver.js to match our design */}
            <style>{`
                .driver-popover {
                    background: white !important;
                    border-radius: 12px !important;
                    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15) !important;
                    max-width: 400px !important;
                    padding: 20px !important;
                    font-family: 'Be Vietnam Pro', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
                }
                .driver-popover-title {
                    font-size: 16px !important;
                    font-weight: 600 !important;
                    color: #1f2937 !important;
                    margin-bottom: 8px !important;
                }
                .driver-popover-description {
                    font-size: 14px !important;
                    color: #6b7280 !important;
                    line-height: 1.6 !important;
                }
                .driver-popover-progress-text {
                    font-size: 12px !important;
                    color: #9ca3af !important;
                }
                .driver-popover-footer {
                    margin-top: 16px !important;
                }
                .driver-popover-navigation-btns {
                    gap: 8px !important;
                }
                .driver-popover-prev-btn,
                .driver-popover-next-btn {
                    padding: 8px 20px !important;
                    border-radius: 6px !important;
                    font-size: 14px !important;
                    font-weight: 500 !important;
                    transition: all 0.2s !important;
                    cursor: pointer !important;
                    text-shadow: none !important;
                    -webkit-font-smoothing: antialiased !important;
                    -moz-osx-font-smoothing: grayscale !important;
                    opacity: 1 !important;
                }
                .driver-popover-prev-btn {
                    background: #f3f4f6 !important;
                    color: #374151 !important;
                    border: 1px solid #e5e7eb !important;
                }
                .driver-popover-prev-btn:hover {
                    background: #e5e7eb !important;
                }
                .driver-popover-next-btn {
                    background: #2563eb !important;
                    color: #ffffff !important;
                    border: none !important;
                    text-shadow: none !important;
                }
                .driver-popover-next-btn:hover {
                    background: #1d4ed8 !important;
                }
                .driver-popover-close-btn {
                    color: #9ca3af !important;
                    width: 28px !important;
                    height: 28px !important;
                }
                .driver-popover-close-btn:hover {
                    color: #374151 !important;
                    background: #f3f4f6 !important;
                    border-radius: 4px !important;
                }
                .driver-popover-arrow-side-left,
                .driver-popover-arrow-side-right,
                .driver-popover-arrow-side-top,
                .driver-popover-arrow-side-bottom {
                    border-color: white !important;
                }
                .driver-overlay {
                    opacity: 1 !important;
                }
                .driver-active-element {
                    opacity: 1 !important;
                    filter: none !important;
                }
            `}</style>
        </TourContext.Provider>
    )
}

export default TourProvider
